/**
 * Ageing a company's way, with the result in front of you before anything is
 * saved (docs/tally/20 §2, "Ageing setup"; the rules in docs/tally/18 §1–§3).
 *
 * Three choices, in the order a finance person makes them: by bill date or by
 * due date; which windows; and, by due date, what a bill without one does.
 * Grace sits under "More", because most companies leave it at nothing.
 *
 * A company that has not chosen how its receivables are aged is asked, never
 * defaulted (`chooseBasis`): neither basis is selected until someone picks
 * one, each said in plain words. Before a save, it shows what the change does
 * to collection work — accounts that would start or stop counting as overdue,
 * and how the money moves between the windows (`impact`) — because changing
 * how money is aged changes who gets chased first.
 *
 * The windows are typed as "up to" days only: each bucket starts the day after
 * the one before ends, so a gap or an overlap cannot be typed at all — and
 * whatever is typed is still checked (`bucketsFromRanges`) before it is used.
 * Every change re-draws the table from the histogram, exactly, before a save.
 * Nothing here changes TallyPrime, a bill's dates or an amount.
 */
import { useEffect, useId, useMemo, useState } from 'react';
import { Button, Disclosure, Segmented, cx, errorClass, fieldClass, helpClass } from '@timely/ui';
import {
    AGEING_LIMITS, AGEING_PRESETS, SETTINGS_LIMITS, ageingChangeImpact, bucketsFromRanges, collectionOverdueFrom, describeSettingsChange, previewBuckets,
    rangesFromBoundaries,
    type AgeHistogram, type AgeingBasis, type CompanyReading, type MissingDueDateRule, type PositionSummary, type ReceivablesSettings, type SourceFacts,
} from '@timely/domain';
import { useFormatters } from '../book/useFormatters';
import { AgeingTable } from './ReceivablesFigures';

export interface AgeingEditorProps {
    histogram: AgeHistogram;
    position: PositionSummary;
    /** The saved settings (or the standard ones), as the editor starts. */
    saved: ReceivablesSettings;
    /** Present only for someone who may save, on a database that can. */
    onSave?: (settings: ReceivablesSettings, reason: string, impact: Record<string, unknown>) => Promise<void>;
    /** A one-line note under the save button (for example, why it cannot be saved yet). */
    note?: string;
    /** The company has not chosen how its receivables are aged: ask, with neither basis selected. */
    chooseBasis?: boolean;
    /** What TallyPrime said at the last update and how it is read now, to show what a change would do before it is saved. */
    impact?: { facts: Pick<SourceFacts, 'asOf' | 'accounts' | 'unmatchedBills'>; current: CompanyReading };
    /** Connecting: the settings as chosen so far (null until valid), saved by Connect rather than here. */
    onDraft?: (draft: ReceivablesSettings | null) => void;
}

/** The two ways to age receivables, in the words a business owner uses. */
export const BASIS_CHOICES: readonly { value: AgeingBasis; label: string; meaning: string }[] = [
    { value: 'bill_date', label: 'Bill date', meaning: 'How long ago the invoice was raised.' },
    { value: 'due_date', label: 'Due date', meaning: 'How long ago payment became due.' },
];

const sameAgeing = (a: ReceivablesSettings, b: ReceivablesSettings) =>
    a.ageing.basis === b.ageing.basis && a.ageing.missingDueDate === b.ageing.missingDueDate
    && a.ageing.boundaries.join(',') === b.ageing.boundaries.join(',') && a.graceDays === b.graceDays;

export function AgeingEditor({ histogram, position, saved, onSave, note, chooseBasis = false, impact, onDraft }: AgeingEditorProps) {
    const { money, count, date } = useFormatters();
    const id = useId();
    const [chosenBasis, setBasis] = useState<AgeingBasis | null>(chooseBasis ? null : saved.ageing.basis);
    const basis: AgeingBasis = chosenBasis ?? saved.ageing.basis;
    const [uptos, setUptos] = useState<string[]>(saved.ageing.boundaries.map(String));
    const [missing, setMissing] = useState<MissingDueDateRule>(saved.ageing.missingDueDate);
    const [grace, setGrace] = useState(String(saved.graceDays));
    const [reason, setReason] = useState('');
    const [busy, setBusy] = useState(false);
    const [saveError, setSaveError] = useState<string | null>(null);
    const [savedNow, setSavedNow] = useState(false);

    const parsed = uptos.map(u => (u.trim() === '' ? NaN : Number(u)));
    // Each bucket starts the day after the one before ends: the "from" is never typed.
    const ranges: { from: number; to: number | null }[] = [];
    for (let from = basis === 'bill_date' ? 0 : 1, i = 0; i <= parsed.length; i++) {
        const to = i < parsed.length ? parsed[i]! : null;
        ranges.push({ from, to });
        if (to !== null) from = Number.isFinite(to) ? to + 1 : from + 1;
    }
    const checked = bucketsFromRanges(ranges, basis);
    const graceDays = Number(grace);
    const graceOk = /^\d{1,2}$/.test(grace.trim()) && graceDays >= 0 && graceDays <= SETTINGS_LIMITS.graceDays;
    const draft: ReceivablesSettings | null = chosenBasis !== null && checked.boundaries && graceOk
        ? { ...saved, ageing: { basis, boundaries: checked.boundaries, missingDueDate: missing }, graceDays }
        : null;
    const preview = draft ? previewBuckets(histogram, draft.ageing) : null;
    // Not yet chosen: any valid choice is a change worth saving, even the standard one.
    const changed = draft !== null && (chooseBasis || !sameAgeing(draft, saved));
    const draftKey = draft ? JSON.stringify(draft) : '';
    useEffect(() => { onDraft?.(draft); }, [draftKey]);   // eslint-disable-line react-hooks/exhaustive-deps
    const effect = useMemo(
        () => (impact && draft && changed ? ageingChangeImpact(impact.facts, impact.current, { settings: draft, rules: 'basis' }) : null),
        [impact, draftKey, changed],   // eslint-disable-line react-hooks/exhaustive-deps
    );
    const presetOf = AGEING_PRESETS.find(p => p.boundaries.join(',') === parsed.join(','))?.id ?? 'custom';

    const pickPreset = (boundaries: readonly number[]) => setUptos(boundaries.map(String));
    const setUpto = (i: number, value: string) => setUptos(u => u.map((x, j) => (j === i ? value.replace(/[^\d]/g, '') : x)));
    const addBucket = () => setUptos(u => {
        const last = Number(u[u.length - 1] ?? 0);
        return u.length + 1 >= AGEING_LIMITS.maxBuckets ? u : [...u, String((Number.isFinite(last) ? last : 0) + 30)];
    });
    const removeBucket = (i: number) => setUptos(u => (u.length <= AGEING_LIMITS.minBuckets - 1 ? u : u.filter((_, j) => j !== i)));

    const save = async () => {
        if (!draft || !onSave || busy) return;
        setBusy(true); setSaveError(null); setSavedNow(false);
        try {
            await onSave(draft, reason, {
                changes: describeSettingsChange(saved, draft),
                buckets: preview?.buckets.map(b => ({ label: b.label, amount: b.amount, bills: b.bills })) ?? [],
                asOf: histogram.asOf,
                ...(effect ? { overdue: { accounts: effect.accounts, becoming: effect.becomingOverdue.length, noLonger: effect.noLongerOverdue.length } } : {}),
            });
            setReason(''); setSavedNow(true);
        } catch (e) { setSaveError(e instanceof Error ? e.message : 'The settings could not be saved just now.'); } finally { setBusy(false); }
    };

    const example = collectionOverdueFrom({ billDate: histogram.asOf, dueDate: histogram.asOf }, { graceDays: graceOk ? graceDays : 0, ageing: { basis, boundaries: [], missingDueDate: missing } });

    const choice = (
        <fieldset className="px-5">
            <legend className="text-body font-semibold text-label mb-1">Age receivables by</legend>
            <p className={cx(helpClass, 'mt-0 mb-2')}>Choose one. Timely does not assume either: it changes which accounts count as overdue.</p>
            <div className="grid gap-2 sm:grid-cols-2 max-w-2xl">
                {BASIS_CHOICES.map(c => (
                    <label key={c.value} className={cx('flex items-start gap-3 rounded-control border px-3.5 py-3 cursor-pointer',
                        chosenBasis === c.value ? 'border-accent bg-accent-tint' : 'border-separator-strong')}>
                        <input type="radio" name={`${id}-basis-choice`} value={c.value} checked={chosenBasis === c.value} onChange={() => setBasis(c.value)} className="mt-1" />
                        <span><span className="block text-body font-semibold text-label">{c.label}</span><span className="block text-secondary text-label-2">{c.meaning}</span></span>
                    </label>
                ))}
            </div>
        </fieldset>
    );
    if (chooseBasis && chosenBasis === null) {
        return (
            <div className="grid gap-5">
                {choice}
                <p className="px-5 text-secondary text-label-3">Choose one to see your receivables aged that way, before anything is saved.</p>
                {note && <p className="px-5 text-caption text-label-3">{note}</p>}
            </div>
        );
    }

    return (
        <div className="grid gap-5">
            {chooseBasis && choice}
            <div className="flex flex-wrap items-center gap-x-6 gap-y-3 px-5">
                {!chooseBasis && (
                    <div>
                        <span className="block text-caption font-medium text-label-3 mb-1" id={`${id}-basis`}>Age bills by</span>
                        <Segmented label="Age bills by" value={basis} onChange={setBasis}
                            options={[{ value: 'bill_date', label: 'Bill date' }, { value: 'due_date', label: 'Due date' }]} />
                        <p className="mt-1 text-caption text-label-3">{BASIS_CHOICES.find(c => c.value === basis)?.meaning}</p>
                    </div>
                )}
                {/* One of four, so the same sliding control as "Age bills by" beside it — not a row of chips with a
                    second idea of what "chosen" looks like (2026-10 global sweep). "Your own" is chosen by typing
                    the days below; pressing it takes you there. */}
                <div>
                    <span className="block text-caption font-medium text-label-3 mb-1">Windows</span>
                    <Segmented
                        label="Ageing windows"
                        className="max-sm:grid max-sm:grid-cols-2"
                        value={presetOf}
                        onChange={choice => {
                            const preset = AGEING_PRESETS.find(p => p.id === choice);
                            if (preset) pickPreset(preset.boundaries);
                            else document.getElementById(`${id}-to-0`)?.focus();
                        }}
                        options={[
                            ...AGEING_PRESETS.map(p => ({
                                value: p.id as string,
                                label: p.boundaries.length === 3 && p.boundaries[0] === 45 ? '45 days' : p.boundaries[0] === 30 ? '30 days' : 'Short',
                                // The preset's full name, as the chips had it: a tooltip, never a second name for the button.
                                hint: p.label,
                            })),
                            { value: 'custom', label: 'Your own' },
                        ]}
                    />
                </div>
            </div>

            <fieldset className="px-5">
                <legend className="text-caption font-medium text-label-3 mb-1">{basis === 'bill_date' ? 'Days since the bill date' : 'Days past the due date'}</legend>
                <ol className="grid gap-2 max-w-md">
                    {ranges.map((r, i) => {
                        const last = i === ranges.length - 1;
                        const problem = checked.problems.find(p => p.bucket === i);
                        return (
                            <li key={i} className="flex flex-wrap items-center gap-2 text-body text-label-2">
                                <span className="w-10 num text-right">{r.from}</span>
                                <span aria-hidden="true">–</span>
                                {last ? (
                                    <span className="text-label-3">and over</span>
                                ) : (
                                    <>
                                        <label className="sr-only" htmlFor={`${id}-to-${i}`}>Bucket {i + 1} ends at day</label>
                                        <input id={`${id}-to-${i}`} inputMode="numeric" value={uptos[i] ?? ''} onChange={e => setUpto(i, e.target.value)}
                                            className={fieldClass({ invalid: Boolean(problem), className: 'w-20 num' })} aria-invalid={Boolean(problem)} />
                                        <span className="text-label-3">days</span>
                                        {ranges.length > AGEING_LIMITS.minBuckets && (
                                            <Button variant="ghost" size="sm" onClick={() => removeBucket(i)} aria-label={`Remove bucket ${i + 1}`}>Remove</Button>
                                        )}
                                    </>
                                )}
                                {problem && <span className={cx(errorClass, 'mt-0')}>{problem.message}</span>}
                            </li>
                        );
                    })}
                </ol>
                {checked.problems.filter(p => p.bucket === undefined).map(p => <p key={p.code} className={errorClass}>{p.message}</p>)}
                {ranges.length < AGEING_LIMITS.maxBuckets && <Button className="mt-2" variant="secondary" size="sm" onClick={addBucket}>Add a bucket</Button>}
                <p className={helpClass}>Each bucket starts the day after the one before it ends, so no day is left out or counted twice. Up to {AGEING_LIMITS.maxBuckets} buckets.</p>
            </fieldset>

            {basis === 'due_date' && (
                <fieldset className="px-5">
                    <legend className="text-caption font-medium text-label-3 mb-1">Bills without a due date from TallyPrime ({count(histogram.noDueDate.bills)}, {money.exact(histogram.noDueDate.paise / 100)})</legend>
                    <div className="grid gap-2">
                        {([['use_bill_date', 'Due on the bill date — TallyPrime’s own rule when there is no credit period'], ['unaged', 'Leave them out of the buckets, as not aged']] as const).map(([value, label]) => (
                            <label key={value} className="flex items-start gap-2 text-body text-label-2 cursor-pointer">
                                <input type="radio" name={`${id}-missing`} checked={missing === value} onChange={() => setMissing(value)} className="mt-1" />
                                <span>{label}</span>
                            </label>
                        ))}
                    </div>
                </fieldset>
            )}

            <div className="px-5">
                <Disclosure title="More" summary={graceOk && graceDays > 0 ? `${graceDays} days’ grace` : 'No grace after the due date'}>
                    <label className="block text-secondary font-medium text-label mb-1.5" htmlFor={`${id}-grace`}>Grace after the due date, in days</label>
                    <input id={`${id}-grace`} inputMode="numeric" value={grace} onChange={e => setGrace(e.target.value.replace(/[^\d]/g, ''))}
                        className={fieldClass({ invalid: !graceOk, className: 'w-24 num' })} aria-invalid={!graceOk} />
                    {!graceOk && <p className={errorClass}>Use a whole number of days from 0 to {SETTINGS_LIMITS.graceDays}.</p>}
                    <p className={helpClass}>
                        TallyPrime’s due date is always shown as it is. Grace only changes when Timely counts a bill as overdue for collections:
                        a bill due {date.day(histogram.asOf)} would count from {example ? date.day(example) : '—'}.
                    </p>
                    <p className={helpClass}>Bills by bill date or by due date stay in the same buckets whatever the grace.</p>
                </Disclosure>
            </div>

            {preview ? (
                <AgeingTable preview={preview} position={position} caption={`Ageing ${basis === 'bill_date' ? 'by bill date' : 'by due date'}, as it would show`} />
            ) : (
                <p className={cx(errorClass, 'px-5')}>Fix the buckets above to see the ageing.</p>
            )}

            {effect && <ImpactNotice effect={effect} />}

            {onSave && (
                <div className="px-5 pb-1 grid gap-2">
                    <label className="block text-secondary font-medium text-label" htmlFor={`${id}-reason`}>Why the change <span className="font-normal text-label-3">(optional, kept with the version)</span></label>
                    <input id={`${id}-reason`} value={reason} maxLength={300} onChange={e => setReason(e.target.value)} className={fieldClass({ className: 'max-w-xl' })} />
                    <div className="flex flex-wrap items-center gap-3">
                        <Button variant="primary" disabled={!changed} busy={busy} onClick={() => void save()}>Save ageing settings</Button>
                        {savedNow && <span role="status" className="text-secondary text-pos">Saved. Every ageing in Timely’s receivables views now uses these windows.</span>}
                        {saveError && <span role="alert" className={errorClass}>{saveError}</span>}
                    </div>
                    <p className={helpClass}>Changes how Timely groups the bills. TallyPrime’s dates and amounts, and every note and promise, stay exactly as they are. Each save is kept as a version, with who and when.</p>
                </div>
            )}
            {note && <p className="px-5 text-caption text-label-3">{note}</p>}
        </div>
    );
}

/** The windows, as they read in a sentence ("0–45, 46–90, 91–135, over 135 days by bill date"). */
export const windowsInWords = (settings: ReceivablesSettings): string => {
    const r = rangesFromBoundaries(settings.ageing.boundaries, settings.ageing.basis);
    return `${r.map(x => (x.to === null ? `over ${x.from - 1}` : `${x.from}–${x.to}`)).join(', ')} days ${settings.ageing.basis === 'bill_date' ? 'by bill date' : 'past the due date'}`;
};

/**
 * What saving would change in collection work: said before the save, because
 * the same bills aged another way change which accounts count as overdue —
 * an account's automatic "Late", the "Older than" filters and who is chased
 * first. Only the accounts the person may see are counted here; the server
 * counts them all and keeps the count with the version.
 */
function ImpactNotice({ effect }: { effect: ReturnType<typeof ageingChangeImpact> }) {
    const { money, count } = useFormatters();
    const sameLayout = effect.before.length === effect.after.length && effect.before.every((b, i) => b.label === effect.after[i]!.label);
    const names = (xs: { name: string }[]) => xs.slice(0, 12).map(x => x.name).join(', ') + (xs.length > 12 ? `, and ${count(xs.length - 12)} more` : '');
    const moved = effect.becomingOverdue.length + effect.noLongerOverdue.length;
    return (
        <section aria-label="What this changes in collection work" className="mx-5 rounded-control border border-separator-strong px-4 py-3">
            <h4 className="text-body font-semibold text-label">If you save this</h4>
            {moved === 0 ? (
                // An age, never "overdue": overdue is money past its due date (Customer 360, semantics.ts).
                <p className="mt-1 text-secondary text-label-2">No account starts or stops having money older than 45 days.</p>
            ) : (
                <ul className="mt-1 grid gap-1 text-secondary text-label-2">
                    <li><span className="font-semibold text-label num">{count(effect.becomingOverdue.length)}</span> {effect.becomingOverdue.length === 1 ? 'account starts' : 'accounts start'} having money older than 45 days{effect.becomingOverdue.length ? `: ${names(effect.becomingOverdue)}` : ''}.</li>
                    <li><span className="font-semibold text-label num">{count(effect.noLongerOverdue.length)}</span> {effect.noLongerOverdue.length === 1 ? 'account stops' : 'accounts stop'} having money older than 45 days{effect.noLongerOverdue.length ? `: ${names(effect.noLongerOverdue)}` : ''}.</li>
                </ul>
            )}
            <p className="mt-1 text-caption text-label-3">An account with money older than 45 days shows as a slow payer unless someone set its rank, appears under "Older than 45 days", and comes first in collection work.</p>
            <table className="mt-3 w-full text-secondary" aria-label="How the money moves between the windows">
                <thead><tr className="text-caption text-label-3"><th className="text-left font-normal py-1">Window</th><th className="text-right font-normal py-1">Now</th><th className="text-right font-normal py-1">After saving</th></tr></thead>
                <tbody>
                    {sameLayout
                        ? effect.after.map((a, i) => (
                            <tr key={a.label} className="border-t border-separator"><th scope="row" className="text-left font-normal py-1 text-label-2">{a.label}</th>
                                <td className="text-right num py-1 text-label-2">{money.exact(effect.before[i]!.amount)}</td><td className="text-right num py-1 text-label">{money.exact(a.amount)}</td></tr>
                        ))
                        : Array.from({ length: Math.max(effect.before.length, effect.after.length) }, (_, i) => (
                            <tr key={i} className="border-t border-separator">
                                <th scope="row" className="text-left font-normal py-1 text-label-2">{effect.before[i] ? effect.before[i]!.label : ''}{effect.before[i] && effect.after[i] ? ' → ' : ''}{effect.after[i] ? effect.after[i]!.label : ''}</th>
                                <td className="text-right num py-1 text-label-2">{effect.before[i] ? money.exact(effect.before[i]!.amount) : ''}</td>
                                <td className="text-right num py-1 text-label">{effect.after[i] ? money.exact(effect.after[i]!.amount) : ''}</td>
                            </tr>
                        ))}
                </tbody>
            </table>
        </section>
    );
}
